import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, UserRound } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { formatDate } from "@/components/public/Shared";

export default function Members() {
  const [data, setData] = useState(null);
  const [ministries, setMinistries] = useState([]);
  const [q, setQ] = useState("");
  const [gender, setGender] = useState("all");
  const [ministry, setMinistry] = useState("all");
  const [visibility, setVisibility] = useState("all");
  const [joinedFrom, setJoinedFrom] = useState("");
  const [joinedTo, setJoinedTo] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 12;

  useEffect(() => {
    api.get("/admin/members/meta")
      .then((r) => setMinistries(r.data.ministries || []))
      .catch((e) => toast.error(apiError(e, "Could not load ministries")));
  }, []);

  const load = useCallback(() => {
    const params = { page, page_size: pageSize };
    if (q) params.q = q;
    if (gender !== "all") params.gender = gender;
    if (ministry !== "all") params.ministry = ministry;
    if (visibility !== "all") params.is_public = visibility;
    if (joinedFrom) params.joined_from = joinedFrom;
    if (joinedTo) params.joined_to = joinedTo;
    api.get("/admin/members", { params }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [q, gender, ministry, visibility, joinedFrom, joinedTo, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;

  return (
    <div data-testid="member-directory" className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold text-navy">Member Directory</h1>
        <p className="mt-1 text-sm text-muted-foreground">Search and filter all church members across every family.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <div className="relative col-span-2">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search name, family ID, phone, ministry…" className="pl-9 bg-white" data-testid="member-search" />
        </div>
        <Select value={gender} onValueChange={(v) => { setGender(v); setPage(1); }}>
          <SelectTrigger className="bg-white" data-testid="member-gender-filter"><SelectValue placeholder="Gender" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All genders</SelectItem>
            <SelectItem value="Male">Male</SelectItem>
            <SelectItem value="Female">Female</SelectItem>
            <SelectItem value="Other">Other</SelectItem>
          </SelectContent>
        </Select>
        <Select value={ministry} onValueChange={(v) => { setMinistry(v); setPage(1); }}>
          <SelectTrigger className="bg-white" data-testid="member-ministry-filter"><SelectValue placeholder="Ministry" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All ministries</SelectItem>
            {ministries.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={visibility} onValueChange={(v) => { setVisibility(v); setPage(1); }}>
          <SelectTrigger className="bg-white" data-testid="member-visibility-filter"><SelectValue placeholder="Visibility" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Public + Private</SelectItem>
            <SelectItem value="true">Public only</SelectItem>
            <SelectItem value="false">Private only</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1">
          <Input type="date" value={joinedFrom} onChange={(e) => { setJoinedFrom(e.target.value); setPage(1); }} className="bg-white text-xs" aria-label="Joined from" data-testid="member-joined-from" />
          <Input type="date" value={joinedTo} onChange={(e) => { setJoinedTo(e.target.value); setPage(1); }} className="bg-white text-xs" aria-label="Joined to" data-testid="member-joined-to" />
        </div>
      </div>

      {!data && <p className="text-sm text-muted-foreground">Loading…</p>}
      {data && data.items.length === 0 && (
        <div className="rounded-lg border border-dashed border-border bg-white/60 px-8 py-14 text-center" data-testid="members-empty">
          <UserRound className="mx-auto h-8 w-8 text-muted-foreground/50" />
          <p className="mt-3 font-heading text-lg font-bold text-navy">No members found</p>
          <p className="mt-1 text-sm text-muted-foreground">Try adjusting your search or filters, or add members from a family page.</p>
        </div>
      )}
      {data && data.items.length > 0 && (
        <>
          <div className="overflow-x-auto rounded-lg border border-border bg-white shadow-sm">
            <table className="w-full text-sm" data-testid="member-table">
              <thead>
                <tr className="border-b border-border bg-secondary/60 text-left">
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Name</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Family</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Gender</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Ministry</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Joined</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Visibility</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.items.map((m) => (
                  <tr key={m.member_id} className="hover:bg-secondary/30" data-testid={`member-dir-row-${m.member_id}`}>
                    <td className="px-4 py-3 font-medium text-navy">{m.full_name}</td>
                    <td className="px-4 py-3">
                      <Link to={`/admin/families/${m.family_id}`} className="text-gold-dark hover:underline" data-testid={`member-family-link-${m.member_id}`}>
                        {m.family_name || m.family_id}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-foreground/80">{m.gender || "—"}</td>
                    <td className="px-4 py-3 text-foreground/80">{m.ministry || "—"}</td>
                    <td className="px-4 py-3 text-foreground/80">{formatDate(m.date_joined) || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${m.is_public ? "bg-green-100 text-green-800" : "bg-muted text-muted-foreground"}`}>
                        {m.is_public ? "Public" : "Private"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between" data-testid="member-pagination">
            <p className="text-xs text-muted-foreground">Page {data.page} of {totalPages} · {data.total} members</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="member-prev-page">Previous</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="member-next-page">Next</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
